/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import { FC } from 'react'
// import { css } from '@emotion/react'
import { useTheme } from '@emotion/react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { rpMediaQueries } from '../../core/theme'
import { BoxStore } from '../../stores/RussianPoker/Box.store'
import { ChipOnBAB } from '../BettingBox/ChipOnBAB'
import BlackSilverBtn from '../BlackSilverBtn'
import { ProtectIcon } from '../Icons'
import { ReactComponent as AnteIcon } from './img/ante.svg'
import { ReactComponent as BetIcon } from './img/bet.svg'
import { ReactComponent as BonusIcon } from './img/bonus.svg'
import { ReactComponent as InsuranceIcon } from './img/insurance.svg'

const Container = styled.div`
  display: flex;
  justify-content: start;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
  position: relative;

  ${rpMediaQueries.tablet} {
    gap: 6px;
    margin-bottom: 8px;
  }
`

const BetCircle = styled.div<{
  hidden?: boolean
}>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};

  ${rpMediaQueries.tablet} {
    width: 35px;
    height: 35px;
  }

  ${rpMediaQueries.desktop} {
    width: 40px;
    height: 40px;
  }
`

const Mark = styled.div`
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${rpMediaQueries.tablet} {
    height: 17px;
  }
`

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  justify-content: center;

  ${rpMediaQueries.tablet} {
    gap: 4px;
  }

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`

const StyledChipOnBAB = styled(ChipOnBAB)`
  position: relative;

  img {
    width: 100%;
    height: 100%;
  }

  span span {
    font-size: 6px;

    ${rpMediaQueries.tablet} {
      font-size: 10px;
    }

    ${rpMediaQueries.desktop} {
      font-size: 12px;
    }
  }
`

const InsuranceWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
`

export const BetsDataView: FC<{
  box: BoxStore
  isInteractive: boolean
}> = observer(({ box, isInteractive }) => {
  const { ui, seatNumber } = box
  const { t } = useTranslation()
  const theme = useTheme()
  const { setInsuranceDecision } = ui
  const ante = box.ante || 0
  const bonus = box.bonus.amount || 0
  const bet = box.bet || 0
  const insurance = box.insurance.cost || 0
  const bonusResult = box.bonus.result
  const insuranceResult = box.insurance.result

  const getIconStyle = (result: 'win' | 'lose' | 'none' | 'refund') => {
    if (result === 'win') {
      return { color: theme.rpTheme.colors.green, opacity: 1 }
    }
    if (result === 'lose') {
      return { color: theme.rpTheme.colors.red, opacity: 1 }
    }
    if (result === 'refund') {
      return { color: theme.rpTheme.colors.gold1, opacity: 1 }
    }
    return { color: 'white', opacity: 0.5 }
  }

  const bonusIconStyle = getIconStyle(bonusResult)
  const insuranceIconStyle = getIconStyle(insuranceResult)
  const isInsuranceAvailable = box.insurance.available && isInteractive

  return (
    <Container>
      <Item>
        <BetCircle hidden={!bonus || box.bonus.result !== 'none'}>
          {bonus > 0 && <StyledChipOnBAB amount={bonus} />}
        </BetCircle>
        <Mark>
          <BonusIcon
            color={bonusIconStyle.color}
            style={{ opacity: bonusIconStyle.opacity }}
          />
        </Mark>
      </Item>
      <Item>
        <BetCircle hidden={!ante}>
          {ante > 0 && <StyledChipOnBAB amount={ante} />}
        </BetCircle>
        <Mark>
          <AnteIcon />
        </Mark>
      </Item>
      <Item>
        <BetCircle hidden={!bet}>
          {bet > 0 && <StyledChipOnBAB amount={bet} />}
        </BetCircle>
        <Mark>
          <BetIcon />
        </Mark>
      </Item>
      {insurance > 0 && (
        <Item>
          <BetCircle hidden={!insurance || box.insurance.result !== 'none'}>
            {insurance > 0 && <StyledChipOnBAB amount={insurance} />}
          </BetCircle>
          <Mark>
            <InsuranceIcon
              color={insuranceIconStyle.color}
              style={{ opacity: insuranceIconStyle.opacity }}
            />
          </Mark>
        </Item>
      )}
      {isInsuranceAvailable && (
        <InsuranceWrapper>
          <motion.div
            key={`insurance-action-${seatNumber}`}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: [0, 1, 1, 0.35, 1], y: [6, 0, 0, 0, 0] }}
            transition={{
              duration: 1.0,
              ease: 'easeOut',
              times: [0, 0.3, 0.5, 0.7, 1.0],
            }}>
            <BlackSilverBtn
              icon={
                <ProtectIcon
                  size={{
                    mobile: '8px',
                    tablet: '14px',
                    desktop: '15px',
                  }}
                />
              }
              text={t('insurance')}
              data-cy={`insurance-btn-${seatNumber}`}
              onClick={setInsuranceDecision}
            />
          </motion.div>
        </InsuranceWrapper>
      )}
    </Container>
  )
})
